import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaHeart,
  FaExclamationTriangle,
  FaPhoneAlt,
  FaHandHoldingHeart,
  FaClock,
  FaChartLine,
  FaBan,
  FaUsers,
} from "react-icons/fa";
import { MdSupportAgent } from "react-icons/md";
import { COUNTRY } from "../config/appConfig";

const ResponsibleGaming = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const tips = [
    {
      icon: FaClock,
      title: "Set Time Limits",
      description:
        "Decide how much time you can spend gaming before you start. Stick to your limits and take regular breaks.",
    },
    {
      icon: FaChartLine,
      title: "Set a Budget",
      description:
        "Only gamble with money you can afford to lose. Never chase losses or borrow money to gamble.",
    },
    {
      icon: FaBan,
      title: "Don't Chase Losses",
      description:
        "Accept that losing is part of gaming. Trying to win back losses often leads to bigger losses.",
    },
    {
      icon: FaUsers,
      title: "Balance Your Life",
      description:
        "Gaming should be entertainment, not your main activity. Keep up with family, friends, and other hobbies.",
    },
  ];

  const warningSignsData = [
    "Spending more money than you can afford",
    "Borrowing money or selling things to gamble",
    "Neglecting work, school, or family responsibilities",
    "Lying about how much you gamble",
    "Feeling restless or irritable when not gambling",
    "Gambling to escape problems or relieve stress",
    "Chasing losses consistently",
    "Failed attempts to cut back or stop",
  ];

  const supportResources = [
    // {
    //   name: "Heads or Tails {COUNTRY} Support",
    //   contact: "+260 76 057 9796",
    //   description: "Our team can help you set limits or self-exclude",
    // },
    // {
    //   name: "National Helpline",
    //   contact: "116 (Toll-free)",
    //   description: "24/7 confidential support for gambling concerns",
    // },
    {
      name: "Gamblers Anonymous",
      contact: "www.gamblersanonymous.org",
      description: "Free peer support and recovery programs",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#071129] to-[#0a1628] py-8 px-4">
      <motion.div
        className="max-w-4xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Back Button */}
        <motion.div variants={itemVariants} className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors duration-300"
          >
            <FaArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30 mb-4">
            <FaHeart className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-red-400 via-orange-400 to-yellow-500 bg-clip-text text-transparent mb-4">
            Responsible Gaming
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Gaming should be fun and entertaining. If it stops being enjoyable,
            it's time to take a step back. We're committed to helping you stay
            in control.
          </p>
        </motion.div>

        {/* Age Restriction Banner */}
        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-2xl p-6 mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center text-2xl font-bold text-red-400 flex-shrink-0">
              18+
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white mb-1">
                Adults Only
              </h2>
              <p className="text-gray-400">
                Heads or Tails {COUNTRY} is strictly for persons 18 years and
                older. We verify ages and will suspend accounts of underage
                users.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Tips Section */}
        <motion.div variants={itemVariants} className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <FaHandHoldingHeart className="text-emerald-400" />
            Tips for Responsible Gaming
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tips.map((tip, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-emerald-500/30 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <tip.icon className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">
                      {tip.title}
                    </h3>
                    <p className="text-gray-400 text-sm">{tip.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Warning Signs */}
        <motion.div variants={itemVariants} className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <FaExclamationTriangle className="text-yellow-400" />
            Warning Signs of Problem Gambling
          </h2>
          <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-2xl p-6">
            <p className="text-gray-400 mb-4">
              If you recognize any of these signs in yourself or someone you
              know, it may be time to seek help:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {warningSignsData.map((sign, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-yellow-400 mt-2 flex-shrink-0" />
                  <p className="text-gray-300 text-sm">{sign}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Self-Exclusion */}
        {/* <motion.div variants={itemVariants} className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <FaBan className="text-red-400" />
            Self-Exclusion Options
          </h2>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <p className="text-gray-400 mb-4">
              If you need a break from gaming, we offer self-exclusion options:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                <span className="text-gray-300">
                  <strong className="text-white">Temporary Exclusion:</strong> Block access for 24 hours, 7 days, or 30 days
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                <span className="text-gray-300">
                  <strong className="text-white">Deposit Limits:</strong> Set daily, weekly, or monthly deposit limits
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                <span className="text-gray-300">
                  <strong className="text-white">Permanent Exclusion:</strong> Close your account permanently
                </span>
              </li>
            </ul>
            <p className="text-gray-500 text-sm">
              Contact our support team to activate any of these options. We're here to help without judgment.
            </p>
          </div>
        </motion.div> */}

        {/* Support Resources */}
        <motion.div variants={itemVariants} className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <MdSupportAgent className="text-cyan-400" />
            Get Help
          </h2>
          <div className="space-y-4">
            {supportResources.map((resource, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-cyan-500/30 transition-all duration-300"
              >
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="font-semibold text-white mb-1">
                      {resource.name}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {resource.description}
                    </p>
                  </div>
                  <a
                    href={
                      resource.contact.startsWith("www")
                        ? `https://${resource.contact}`
                        : `tel:${resource.contact}`
                    }
                    target={
                      resource.contact.startsWith("www") ? "_blank" : undefined
                    }
                    rel={
                      resource.contact.startsWith("www")
                        ? "noopener noreferrer"
                        : undefined
                    }
                    className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors duration-300"
                  >
                    <FaPhoneAlt className="w-4 h-4" />
                    <span>{resource.contact}</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Commitment Statement */}
        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/30 rounded-2xl p-6 text-center"
        >
          <h3 className="text-xl font-semibold text-white mb-3">
            Our Commitment
          </h3>
          <p className="text-gray-400 max-w-2xl mx-auto">
            At Heads or Tails {COUNTRY}, we are committed to providing a safe
            and enjoyable gaming environment. We actively monitor for signs of
            problem gambling and provide tools to help you stay in control. Your
            well-being is our priority.
          </p>
        </motion.div>

        {/* Footer */}
        <motion.div
          variants={itemVariants}
          className="mt-12 text-center text-gray-500 text-sm"
        >
          <p>
            Remember: Gaming should always be fun. When the fun stops, stop.
          </p>
          {/* <p className="mt-2">
            Need help? Call us at{" "}
            <a
              href="tel:+260971234567"
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              +260 76 057 9796
            </a>
          </p> */}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ResponsibleGaming;
